import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem, Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { splitIsoDateTime } from '../lib/dateTimeIso';
import { MEETING_TYPES } from './MeetingDialog';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

// Meeting-Typen aus der zentralen Definition (MeetingDialog = Single Source,
// synchron mit dem pm-api-ENUM). Frühere Eigenliste war veraltet (Workshop/Review/…)
// und führte beim Anlegen zu einem Enum-Fehler.
export { MEETING_TYPES };
const DEFAULT_MEETING_TYPE = 'Team Meeting';

export interface MeetingEditValues {
  titel: string;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:mm
  typ: string;
}

export interface EditableMeeting {
  id: string;
  titel: string | null;
  typ: string;
  datum: string | null; // ISO datetime
}

/**
 * Schlanker Bearbeiten-Dialog fuer ein AP-Meeting (Titel, Datum, Uhrzeit, Typ)
 * mit Loeschen-Aktion. Analog zu TaskEditDialog.
 */
export function MeetingEditDialog({
  open, meeting, onSave, onDelete, onClose,
}: {
  open: boolean;
  meeting: EditableMeeting | null;
  onSave: (meetingId: string, values: MeetingEditValues) => Promise<void>;
  onDelete: (meetingId: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const [form, setForm] = useState<MeetingEditValues>({ titel: '', date: '', time: '', typ: DEFAULT_MEETING_TYPE });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && meeting) {
      const { date, time } = splitIsoDateTime(meeting.datum);
      setForm({ titel: meeting.titel ?? '', date, time, typ: meeting.typ || DEFAULT_MEETING_TYPE });
    }
  }, [open, meeting]);

  const canSave = form.date.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || busy || !meeting) return;
    setBusy(true);
    try { await onSave(meeting.id, { ...form, titel: form.titel.trim() }); onClose(); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (busy || !meeting) return;
    setBusy(true);
    try { await onDelete(meeting.id); onClose(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <AppDialogTitle>{t('meetings.editTitle', 'Meeting bearbeiten')}</AppDialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={t('meetings.titel', 'Titel')} value={form.titel} fullWidth
            onChange={(e) => setForm((f) => ({ ...f, titel: e.target.value }))}
          />
          <TextField
            type="date" label={t('meetings.date', 'Datum')} required fullWidth
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="time" label={t('meetings.time', 'Uhrzeit')} fullWidth
            value={form.time}
            onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('meetings.typ', 'Typ')}</InputLabel>
            <Select
              value={form.typ} label={t('meetings.typ', 'Typ')}
              onChange={(e) => setForm((f) => ({ ...f, typ: e.target.value }))}
            >
              {MEETING_TYPES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={busy}>
          {tc('actions.delete', 'Löschen')}
        </Button>
        <Box>
          <Button onClick={onClose} disabled={busy}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave || busy}>
            {tc('actions.save', 'Speichern')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
